import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import { styled } from '@mui/material/styles'
import * as R from 'ramda'
import {
  type Constant,
  type Field,
  LanguageUtils,
  type Nil,
  type Question,
  type QuestionAnswer,
} from '@pbt/pbt-ui-components'

import Typography from '~/components/elements/Typography/Typography'
import { getAnswerInputTypes } from '~/store/reducers/constants'

import QuestionInput from './QuestionInput'
import QuestionTextField from './QuestionTextField'
import { getAnswerTypeFlags } from './questionUtils'

const StyledTypography = styled(Typography.H3)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}))

const AnswerFollowUp = ({
  followUpQuestionInputId,
  followUpQuestion,
  setFollowUpQuestion,
}: {
  followUpQuestion: Partial<Question> | Nil
  followUpQuestionInputId: Field
  setFollowUpQuestion: React.Dispatch<
    React.SetStateAction<Partial<Question> | Nil>
  >
}) => {
  const { t } = useTranslation(['Admin', 'Common'])

  const AnswerInputTypes: Constant[] = useSelector(getAnswerInputTypes)
  const { isMultipleSelectionType } = getAnswerTypeFlags(
    followUpQuestionInputId.value,
    AnswerInputTypes,
  )

  const onAddAnswerRequested = () => {
    const updatedAnswers = (followUpQuestion?.answers || []).concat({
      text: '',
    } as QuestionAnswer)
    setFollowUpQuestion({
      ...followUpQuestion,
      answers: updatedAnswers,
    } as Partial<Question>)
  }

  const handleMultiSelectionEditAnswer = (
    updatedAnswer: any,
    index: number | Nil,
  ) => {
    const currentAnswers: string | readonly any[] =
      followUpQuestion?.answers || []
    const validIndex =
      !R.isNil(index) && index < currentAnswers.length
        ? index
        : currentAnswers.length
    const finalAnswer = R.update(validIndex, updatedAnswer, currentAnswers)
    setFollowUpQuestion({
      ...followUpQuestion,
      answers: finalAnswer,
    } as Partial<Question>)
  }

  const questionText = followUpQuestion?.questionText || ''
  const questionSubText = followUpQuestion?.questionSubText || ''

  const answerSubHeader = isMultipleSelectionType ? (
    <StyledTypography color="brandSecondary">
      {t('Admin:CATALOG.ANSWER_POPUP.FOLLOW_UP_OPTIONS_SECTION')}
    </StyledTypography>
  ) : null

  return (
    <Grid>
      <Grid item mt={2}>
        <Typography.H3 color="brandSecondary">
          {t('Admin:CATALOG.ANSWER_POPUP.INCLUDE_FOLLOW_UP_QUESTION')}
        </Typography.H3>
        <RadioGroup
          value={followUpQuestionInputId.value}
          onChange={(_, value) => {
            followUpQuestionInputId.setValue(value)
            setFollowUpQuestion(
              (state) =>
                ({
                  ...state,
                  followUp: true,
                  answerInputTypeId: value,
                  answers: [{ inputs: [{ inputTypeId: value }] }],
                }) as Partial<Question>,
            )
          }}
        >
          {AnswerInputTypes.map((type) => (
            <div key={type.id}>
              <FormControlLabel
                control={<Radio sx={{ padding: 0.5 }} />}
                label={LanguageUtils.getTranslatedFieldName(type)}
                sx={{
                  '.MuiFormControlLabel-root': {
                    margin: 0,
                  },
                  '.MuiFormControlLabel-label': {
                    color: 'grayGray1',
                    marginLeft: 0.5,
                  },
                }}
                value={type.id}
              />
              {followUpQuestionInputId.value === type.id && (
                <>
                  <Grid item sx={{ ml: 3.5 }}>
                    <StyledTypography color="brandSecondary">
                      {t('Admin:CATALOG.ANSWER_POPUP.FOLLOW_UP_QUESTION_FIELD')}
                    </StyledTypography>
                    <QuestionTextField
                      disabled={false}
                      initialText={questionText}
                      placeholder="Type your clarifying question here*"
                      onUpdate={(value) => {
                        setFollowUpQuestion((state) => ({
                          ...state,
                          questionText: value,
                        }))
                      }}
                    />
                    <StyledTypography color="brandSecondary">
                      {t(
                        'Admin:CATALOG.ANSWER_POPUP.FOLLOW_UP_QUESTION_SUBTEXT_FIELD',
                      )}
                    </StyledTypography>
                    <QuestionTextField
                      disabled={false}
                      initialText={questionSubText}
                      placeholder="Optional. Add secondary copy for question."
                      onUpdate={(value) => {
                        setFollowUpQuestion((state) => ({
                          ...state,
                          questionSubText: value,
                        }))
                      }}
                    />
                    {answerSubHeader}
                  </Grid>
                  <Grid>
                    <QuestionInput
                      buttonText={t('Common:ADD_OPTION')}
                      disableAnswer={false}
                      handleEditAnswer={handleMultiSelectionEditAnswer}
                      inputType={type.id}
                      placeholder={t(
                        'Admin:CATALOG.ANSWER_POPUP.FOLLOW_UP_OPTION_PLACEHOLDER',
                      )}
                      question={followUpQuestion}
                      setQuestionCandidate={setFollowUpQuestion}
                      onAddAnswerRequested={onAddAnswerRequested}
                    />
                  </Grid>
                </>
              )}
            </div>
          ))}
        </RadioGroup>
      </Grid>
    </Grid>
  )
}

export default AnswerFollowUp
